import * as Sentry from '@sentry/react';

/**
 * Business-level error monitoring helpers.
 *
 * Use these instead of bare `console.error` when something goes wrong
 * during a meaningful user flow (booking failed, payment failed, AI fail, etc.).
 * Each helper attaches the right tags so you can filter in Sentry.
 */

type Severity = 'fatal' | 'error' | 'warning' | 'info';

interface ReportOptions {
  /** Tags become filters in Sentry (e.g. landlord_id, booking_id). */
  tags?: Record<string, string | number | boolean | undefined | null>;
  /** Extra data shown on the issue page. Free-form. */
  extra?: Record<string, unknown>;
  /** Maps to Sentry severity. Default: 'error'. */
  level?: Severity;
  /** Group similar errors under a single issue. */
  fingerprint?: string[];
}

function withScope(opts: ReportOptions | undefined, fn: () => void) {
  Sentry.withScope((scope) => {
    if (opts?.level) scope.setLevel(opts.level);
    if (opts?.fingerprint) scope.setFingerprint(opts.fingerprint);
    if (opts?.tags) {
      for (const [k, v] of Object.entries(opts.tags)) {
        if (v !== undefined && v !== null) scope.setTag(k, String(v));
      }
    }
    if (opts?.extra) {
      for (const [k, v] of Object.entries(opts.extra)) {
        scope.setExtra(k, v);
      }
    }
    fn();
  });
}

/** Report a thrown Error with extra business context. */
export function reportError(error: unknown, opts?: ReportOptions) {
  if (import.meta.env.DEV) {
     
    console.error('[reportError]', error, opts);
  }
  withScope(opts, () => {
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), opts?.level ?? 'error');
    }
  });
}

/** Report a non-throw business event (e.g. "guest entered wrong code 3x"). */
export function reportEvent(message: string, opts?: ReportOptions) {
  if (import.meta.env.DEV) {
     
    console.warn('[reportEvent]', message, opts);
  }
  withScope(opts, () => {
    Sentry.captureMessage(message, opts?.level ?? 'warning');
  });
}

/** Add a breadcrumb (audit trail of what user did before the error). */
export function addBreadcrumb(
  message: string,
  category: 'booking' | 'payment' | 'auth' | 'guest' | 'chat' | 'navigation' | 'ui' = 'ui',
  data?: Record<string, unknown>,
) {
  Sentry.addBreadcrumb({ message, category, data, level: 'info' });
}

// ---------- Pre-built helpers for common BookiApp flows ----------

export function reportBookingError(
  error: unknown,
  ctx: { bookingId?: string; landlordId?: string; unitId?: string; action: string },
) {
  reportError(error, {
    tags: {
      area: 'bookings',
      action: ctx.action,
      booking_id: ctx.bookingId,
      landlord_id: ctx.landlordId,
      unit_id: ctx.unitId,
    },
    fingerprint: ['booking', ctx.action],
  });
}

export function reportPaymentError(
  error: unknown,
  ctx: { bookingId?: string; landlordId?: string; amount?: number; method?: string },
) {
  reportError(error, {
    level: 'error',
    tags: {
      area: 'payments',
      booking_id: ctx.bookingId,
      landlord_id: ctx.landlordId,
      method: ctx.method,
    },
    extra: { amount: ctx.amount },
    fingerprint: ['payment', ctx.method ?? 'unknown'],
  });
}

export function reportGuestPortalError(
  error: unknown,
  ctx: { token?: string; bookingId?: string; action: string },
) {
  reportError(error, {
    tags: {
      area: 'guest_portal',
      action: ctx.action,
      booking_id: ctx.bookingId,
      // Don't put the full token as a tag (PII). Only suffix for grouping.
      token_suffix: ctx.token?.slice(-6),
    },
    fingerprint: ['guest_portal', ctx.action],
  });
}

export function reportAIError(
  error: unknown,
  ctx: { feature: 'guest_assistant' | 'landlord_assistant' | 'translate' | 'scan_booking' | 'recommendations'; landlordId?: string },
) {
  reportError(error, {
    tags: {
      area: 'ai',
      feature: ctx.feature,
      landlord_id: ctx.landlordId,
    },
    fingerprint: ['ai', ctx.feature],
  });
}

export function reportEdgeFunctionError(
  error: unknown,
  ctx: { functionName: string; status?: number; landlordId?: string },
) {
  reportError(error, {
    tags: {
      area: 'edge_function',
      function: ctx.functionName,
      status: ctx.status,
      landlord_id: ctx.landlordId,
    },
    fingerprint: ['edge_function', ctx.functionName],
  });
}
